import React, { useCallback, useMemo } from 'react'
import { Box } from '@mui/material'
import { createColumnHelper } from '@tanstack/react-table'
import { generatePath } from 'react-router-dom'

import { useGetSbpPaymentsByMethods2B } from '@/services'
import { TableComponent } from '@/components/table'
import { SbpPayment } from '@/types.ts'
import { APP_ROUTES } from '@/constants.ts'
import { FetchErrorMessage } from '@/components'
import { router } from '@/router.tsx'
import { DefaultFiltersState, TableSort } from '@/stores'
import { useIsMobile } from '@/hooks'
import { useAppDispatch, useAppSelector } from '@/stores/hooks.ts'
import { sbpPaymentsActions, sbpPaymentSelectors } from '@/stores/slices/sbp-payments-slice.ts'
import { userSelectors } from '@/stores/slices/user-slice.ts'

import {
  defaultSettings,
  getColumnsSettings,
  getStaticColumns,
  KEY_PAGE,
  PartialSbpPayment,
} from '../default-settings.tsx'

const columnHelper = createColumnHelper<SbpPayment>()
const defaultColumns = getColumnsSettings(columnHelper)
const staticColumns = getStaticColumns(columnHelper)
const defaultSettingsKey = Object.keys(defaultSettings)

export const ToBPaymentsTable = (): React.ReactElement => {
  const settings = useAppSelector((state) => userSelectors.personalSettings(state, KEY_PAGE))
  const { data, isLoading, isFetching, isError, refetch } = useGetSbpPaymentsByMethods2B()
  const isMobile = useIsMobile()
  const { page, showCount, sort } = useAppSelector(sbpPaymentSelectors.filters)
  const dispatch = useAppDispatch()

  const handlePaginationParam = (param: keyof DefaultFiltersState) => (value: number) =>
    dispatch(sbpPaymentsActions.setFilter({ filterKey: param, filterValue: value }))
  const handleSort = (nextSort: TableSort) =>
    dispatch(sbpPaymentsActions.setFilter({ filterKey: 'sort', filterValue: nextSort }))
  const handleViewTransaction = useCallback(
    (row: SbpPayment) => () => {
      router.navigate(generatePath(APP_ROUTES.SbpPaymentView, { id: row.operationId.toString() }))
    },
    []
  )

  const sbpPaymentCols = useMemo(() => {
    const format = isMobile ? 'mobile' : 'desktop'

    const desktopSettings = (settings?.[format] ?? defaultSettingsKey) as Array<keyof PartialSbpPayment>
    const dynamicSettings = desktopSettings.filter((e) => defaultSettingsKey.includes(e)).map((e) => defaultColumns[e])
    return [...dynamicSettings, ...Object.values(staticColumns)]
  }, [settings, isMobile])

  return (
    <FetchErrorMessage isError={isError} refetch={refetch} isFetching={isFetching}>
      <Box mt={3}>
        <TableComponent<SbpPayment>
          rowKey="operationId"
          columns={sbpPaymentCols}
          page={page}
          isLoading={isLoading || isFetching}
          columnPinning={{ right: ['refund-action'] }}
          sort={sort}
          size={showCount}
          data={data?.data?.content ?? []}
          totalPages={data?.data?.totalPages}
          onPageChange={handlePaginationParam('page')}
          onRowClick={handleViewTransaction}
          onSizeChange={handlePaginationParam('showCount')}
          onSort={handleSort}
        />
      </Box>
    </FetchErrorMessage>
  )
}
